import React, { useEffect } from 'react';
import {
    Trophy,
    ShieldCheck,
    Users,
    Wallet,
    Target,
    FileText,
    Mic,
    Video,
    Eye,
    LayoutDashboard,
    Flame,
    FolderOpen,
    Wrench,
    Bot,
    Clock,
    TrendingUp,
    Building2
} from 'lucide-react';

const InfoCard = ({ icon: Icon, heading, subheading, accent = "indigo" }) => {
    const accents = {
        indigo: {
            bg: "bg-indigo-900/10",
            border: "border-indigo-500/30",
            iconBg: "bg-indigo-500/10",
            iconColor: "text-indigo-300"
        },
        rose: {
            bg: "bg-rose-900/10",
            border: "border-rose-500/30",
            iconBg: "bg-rose-500/10",
            iconColor: "text-rose-300"
        },
        emerald: {
            bg: "bg-emerald-900/10",
            border: "border-emerald-500/30",
            iconBg: "bg-emerald-500/10",
            iconColor: "text-emerald-300"
        },
        amber: {
            bg: "bg-amber-900/10",
            border: "border-amber-500/30",
            iconBg: "bg-amber-500/10",
            iconColor: "text-amber-300"
        }
    };
    const a = accents[accent];
    return (
        <div className={`rounded-2xl border ${a.border} ${a.bg} p-6 sm:p-7 flex flex-col`}>
            <div className={`${a.iconBg} ${a.iconColor} w-11 h-11 rounded-xl flex items-center justify-center mb-5`}>
                <Icon className="w-5 h-5" />
            </div>
            <h3 className="text-base sm:text-lg font-black text-white mb-2 leading-snug">{heading}</h3>
            <p className={`text-xs sm:text-sm font-medium leading-snug ${accent === 'rose' ? 'text-rose-100/70' : 'text-white/55'}`}>{subheading}</p>
        </div>
    );
};

const SectionEyebrow = ({ children, color = "indigo" }) => {
    const colors = {
        indigo: "text-indigo-300/80",
        rose: "text-rose-300/80",
        amber: "text-amber-300/80",
        emerald: "text-emerald-300/80"
    };
    return (
        <div className={`text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase mb-4 ${colors[color]}`}>
            {children}
        </div>
    );
};

const NumRow = ({ n, h, s }) => (
    <div className="flex items-start gap-5 sm:gap-7 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-5 sm:p-6">
        <div className="text-2xl sm:text-3xl font-black text-indigo-400/80 shrink-0 w-10 sm:w-12 tabular-nums">{n}</div>
        <div>
            <h3 className="text-base sm:text-lg font-black text-white mb-1 leading-snug">{h}</h3>
            <p className="text-xs sm:text-sm text-white/55 font-medium leading-snug">{s}</p>
        </div>
    </div>
);

export default function FarazStrategy() {

    useEffect(() => {
        document.title = "Faraz Ansari | Brand Research";
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pt-36 sm:pt-40 pb-20 relative z-10">

            {/* Floating Header */}
            <header className="fixed top-0 left-0 right-0 z-50 p-4 sm:p-6 flex justify-between items-center bg-transparent pointer-events-none">
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-3 sm:px-4 py-2 rounded-full pointer-events-auto shadow-lg">
                    <span className="font-bold tracking-tight text-white text-sm sm:text-base">Social<span className="text-indigo-400">SEO</span></span>
                    <span className="text-white/30 mx-1.5 sm:mx-2">/</span>
                    <span className="text-white/70 font-medium text-xs sm:text-sm">Faraz Ansari</span>
                </div>
                <div className="bg-indigo-500/20 backdrop-blur-lg border border-indigo-500/30 px-2.5 sm:px-3 py-1 rounded-full pointer-events-auto">
                    <span className="text-indigo-300 text-[10px] sm:text-xs font-bold tracking-widest uppercase">Research</span>
                </div>
            </header>

            <main className="max-w-[1080px] mx-auto px-6 space-y-28 sm:space-y-40">

                {/* HERO */}
                <section className="text-center pt-12 sm:pt-20">
                    <div className="text-indigo-300/70 text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase mb-10">
                        Built from your questionnaire · 8 Sep 2026
                    </div>

                    <h1 className="text-5xl sm:text-6xl md:text-7xl font-black tracking-tight mb-8 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400 leading-[1.05] pb-2">
                        The research<br />behind your brand.
                    </h1>

                    <p className="text-base sm:text-lg md:text-xl text-white/55 max-w-2xl mx-auto leading-relaxed mb-6 font-light">
                        Your positioning, your story spine, your content series and the system that runs it.<br className="hidden sm:block" /> Everything below is built from your questionnaire, our call and our read of your market. Nothing is templated.
                    </p>
                </section>

                {/* QUESTIONNAIRE READ */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="indigo">The questionnaire read</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            What your answers<br />actually told us.
                        </h2>
                        <p className="text-base sm:text-lg text-white/55 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            You answered nine of twenty questions with some version of "I don't know, help me get clarity." That was the most useful data in the document. It told us exactly where you need a partner, not a vendor.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 max-w-5xl mx-auto">
                        <InfoCard icon={ShieldCheck} heading="What you already know" subheading="your thesis, your ICP, your enemy and your proof · the four things a brand cannot be built without, and you gave us all four" accent="indigo" />
                        <InfoCard icon={Wrench} heading="What we designed for you" subheading="formats, series names, hooks, CTAs, posting rhythm · every blank you left is filled on this page, from your raw material" accent="indigo" />
                        <InfoCard icon={Eye} heading="What we lock together" subheading="polarizing takes, credentials framing and collaboration rules · the judgment calls that get decided in onboarding week, with you in the room" accent="indigo" />
                    </div>
                </section>

                {/* POSITIONING */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="indigo">The positioning</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            The engineer who was here<br />before the bubble.
                        </h2>
                        <p className="text-base sm:text-lg text-white/55 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            The internet is full of people selling AI. Almost none of them have run production systems. You said it yourself: building is getting easy, distribution is the moat. Your brand is the anti-guru position, and your resume is the proof nobody can copy.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 max-w-5xl mx-auto">
                        <InfoCard icon={Trophy} heading="7 years before the hype" subheading="full-stack engineer across startups, a package near 1 Cr with ESOPs · you knew systems before AI was a headline" accent="indigo" />
                        <InfoCard icon={Wallet} heading="A real exit" subheading="built and sold your e-commerce company for USD 50,000 · you have done the thing your audience wants to do" accent="indigo" />
                        <InfoCard icon={Building2} heading="Systems in production" subheading="sales-call QA on your own CRM, medical-records automation, SEO publishing · live businesses run on what you build" accent="indigo" />
                    </div>
                </section>

                {/* AUDIENCE */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="rose">The audience read</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            Who this brand is for.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 max-w-5xl mx-auto">
                        <InfoCard icon={Users} heading="The owner with a real business" subheading="healthcare, legal, construction, auto · runs a traditional business, knows AI matters, cannot tell builders from sellers" accent="rose" />
                        <InfoCard icon={Flame} heading="Their fear, your fuel" subheading="you named it: they see competitors using AI to pull ahead and worry they are falling behind · every video answers that fear with something real" accent="rose" />
                        <InfoCard icon={Target} heading="One viewer per script" subheading="we write every video for one specific owner, not for everyone interested in AI · narrow problem, broad hook, complete answer" accent="rose" />
                    </div>

                    <div className="max-w-3xl mx-auto space-y-4 mt-10">
                        <NumRow n="01" h="The gap in your market" s="You named 360 Labs as your competitor. They sell as a studio, to legacy enterprises, in company language. Nobody in this space owns the face-and-name trust position for the small business owner. That seat is empty, and this brand takes it." />
                        <NumRow n="02" h="The trust problem you solve" s="Your buyer has seen the 99-rupee automation course and the workflow demo that dies in production. They are not skeptical of AI. They are skeptical of the people selling it. A brand built on shipped systems is the only answer they have not seen." />
                        <NumRow n="03" h="Why deep beats wide" s="You said it on the questionnaire: they want deep automation, not surface-level. So the content goes deep on one business type at a time. A garage owner who sees his exact workflow rebuilt on screen does not keep scrolling." />
                    </div>
                </section>

                {/* STORY SPINE */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="indigo">The story spine</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            Three chapters<br />every video draws from.
                        </h2>
                    </div>

                    <div className="max-w-3xl mx-auto space-y-4">
                        <NumRow n="01" h="The Honest Exit" s="You quit your first job with no offer in hand because clients were being billed for senior talent and given juniors. This chapter is your character. It is why people will trust your takes on an industry full of tricks." />
                        <NumRow n="02" h="The Climb" s="Scaler grind at 11 hours a day, 8 lakhs to 20 lakhs to a package near 1 Cr. This chapter is your competence, and it earns you the right to call out the shortcut sellers." />
                        <NumRow n="03" h="The Bet" s="You walked away from the 1 Cr job to build your own. Sold one company, parked another, still betting. This chapter is what your audience wants to believe about themselves, so it is the one they share." />
                    </div>
                </section>

                {/* CONTENT SERIES */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="indigo">The content series</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            Four series.<br />No more guessing.
                        </h2>
                        <p className="text-base sm:text-lg text-white/55 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            You answered "I don't know" to the format questions. Good. That is our job. These four series come from what you did give us, and they run on Instagram, Twitter and LinkedIn.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 max-w-5xl mx-auto">
                        <InfoCard icon={Wrench} heading="Real Builds" subheading="the automations you have already shipped, broken down · what it does, what it replaced, what it saves · your strongest answer becomes your strongest series" accent="amber" />
                        <InfoCard icon={Flame} heading="Guru Autopsy" subheading="why the workflow they were sold falls over in production, from someone whose systems move real money · the myth-busting you asked for, fact-checked before it ships" accent="amber" />
                        <InfoCard icon={TrendingUp} heading="The Risk Log" subheading="the three chapters above, told one decision at a time · the story series that makes strangers feel like they know you" accent="amber" />
                        <InfoCard icon={FolderOpen} heading="The Build Log" subheading="your current bets, built in public · distribution before product, which is your whole thesis, proven on your own page" accent="amber" />
                    </div>
                </section>

                {/* THE SYSTEM */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="emerald">The system</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            Your brand runs<br />without your calendar.
                        </h2>
                        <p className="text-base sm:text-lg text-white/55 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            Built around what you told us: you can show up for approvals and the occasional shoot, and we design the flow and the discipline around it.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 max-w-5xl mx-auto">
                        <InfoCard icon={Mic} heading="You drop voice notes" subheading="a build finishes, a take hits, you open WhatsApp · we ask the right questions to pull the insight out of you" accent="emerald" />
                        <InfoCard icon={FileText} heading="We script in your voice" subheading="hooks, structure, words · every script from your real projects and experience, never generic filler" accent="emerald" />
                        <InfoCard icon={Bot} heading="Your clone records" subheading="trained on our fine-tuned LoRA, the approach you liked on the call · no lip-sync glitches, no exaggerated impressions, no filming days" accent="emerald" />
                        <InfoCard icon={Eye} heading="You approve, we ship" subheading="nothing posts without your sign-off · daily cadence across Instagram, Twitter and LinkedIn" accent="emerald" />
                        <InfoCard icon={LayoutDashboard} heading="The setter works your DMs" subheading="Meta-verified dashboard · comments become qualified DMs become booked calls · we bring the horse to the water, closing stays yours" accent="emerald" />
                        <InfoCard icon={Clock} heading="Proof inside 15 days" subheading="you said you know within 15 days whether something adds value · the engine is live and posting by day 14, so you judge us on your own clock" accent="emerald" />
                    </div>
                </section>

                {/* 90 DAYS + GUARANTEE */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="amber">The 90 days</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            The roadmap<br />and the guarantee.
                        </h2>
                    </div>

                    <div className="max-w-3xl mx-auto space-y-4 mb-10">
                        <NumRow n="W1" h="Onboarding" s="Clone training on your voice and face, positioning locked, the judgment calls decided, first scripts written. The heaviest back and forth happens here, on purpose." />
                        <NumRow n="W2" h="The engine goes live" s="Daily posting starts across Instagram, Twitter and LinkedIn. You said you know within 15 days whether something adds value. This is why the engine is live by day 14." />
                        <NumRow n="W3-8" h="Test, then double down" s="Series and hooks tested against real data, not opinion. What pulls gets volume. The setter and dashboard go live once the audience is real." />
                        <NumRow n="W9-12" h="Compound" s="Winning formats scale and the follower target lands. By now the brand is a distribution asset that carries every bet you make next." />
                    </div>

                    <div className="max-w-3xl mx-auto">
                        <div className="rounded-2xl border border-amber-500/20 bg-amber-900/10 p-6 sm:p-8 text-center">
                            <div className="text-amber-300/80 text-[10px] sm:text-xs font-bold tracking-[0.25em] uppercase mb-4">The guarantee</div>
                            <p className="text-sm sm:text-base text-white/60 font-light leading-relaxed">
                                10,000 followers in 90 days, written into a legally bound contract. If we miss it, you get 100% of your money back.
                            </p>
                        </div>
                    </div>
                </section>

                {/* FINAL CTA */}
                <section className="text-center pt-4 sm:pt-8">
                    <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-white leading-tight mb-6">
                        We lock the number on our call.
                    </h2>
                    <p className="text-base sm:text-lg text-white/55 max-w-xl mx-auto font-light leading-relaxed mb-10">
                        The research is done and the system is ready. Week 1 starts the day we sign.
                    </p>
                    <a
                        href="mailto:suresh@socialseo.in"
                        className="inline-block bg-indigo-500 hover:bg-indigo-400 transition text-white font-bold px-8 py-4 rounded-full text-sm sm:text-base"
                    >
                        suresh@socialseo.in
                    </a>
                </section>

                {/* FOOTER */}
                <footer className="text-center text-slate-500 text-xs sm:text-sm pt-20 border-t border-white/5">
                    <div className="font-bold text-white/60 mb-3 tracking-wider">SocialSEO LLP</div>
                    <div>
                        <a href="mailto:suresh@socialseo.in" className="text-indigo-300/80 hover:text-indigo-200 transition">suresh@socialseo.in</a>
                    </div>
                </footer>

            </main>
        </div>
    );
}
